import * as React from 'react';
import { EditState, EditProps, BaseRouteProps } from '../../interfaces';
import { Organization, Tenant } from '../../models';
import { OrganizationDataService, TenantDataService } from '../../services';
import { NavLink } from 'react-router-dom';
import { OrganizationForm } from './OrganizationForm';
import { LoadingIndicator } from '../../components';
import { UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

interface OrganizationEditState extends EditState<Organization> {
    tenants: Tenant[];
    loading: boolean;
}

interface OrganizationEditProps extends BaseRouteProps<EditProps> {
    UserStore?: UserStore;
}

@inject('UserStore') @observer
export class OrganizationEdit extends React.Component<OrganizationEditProps, OrganizationEditState> {
    private organizationDataService = new OrganizationDataService(this.props.notificationService);
    private tenantDataService = new TenantDataService(this.props.notificationService);

    constructor(props: OrganizationEditProps) {
        super(props);

        this.state = {
            originalEntity: undefined,
            tenants: new Array<Tenant>(),
            loading: false
        };

        this.save = this.save.bind(this);
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    public componentDidMount() {
        const id = this.props.match.params.id;

        if (id) {
            this.organizationDataService.get(id).then((organization: Organization) => {
                this.setState({ originalEntity: organization });
            });
        }

        this.tenantDataService.getAll().then((tenants: Tenant[]) => {
            this.setState({ tenants: tenants });
        });
    }

    private deactivate() {
        const confirmed = confirm(`Are you sure you want to deactivate ${this.state.originalEntity!.name}? This will prevent members of this organization from accessing the system, but will continue to include their data within reports.`);

        if (confirmed && this.props.match.params.id) {
            this.organizationDataService.deactivate(this.props.match.params.id).then(
                () => {
                    this.setState({ originalEntity: Object.assign({}, this.state.originalEntity, { isDeactivated: true }) });
                },
                (err) => {
                    console.error(err);
                }
            );
        }
    }

    private reactivate() {
        const confirmed = confirm(`Are you sure you want to reactivate ${this.state.originalEntity!.name}`);

        if (confirmed && this.props.match.params.id) {
            this.organizationDataService.reactivate(this.props.match.params.id).then(
                () => {
                    this.setState({ originalEntity: Object.assign({}, this.state.originalEntity, { isDeactivated: false }) });
                },
                (err) => {
                    console.error(err);
                }
            );
        }
    }

    private delete() {
        const confirmed = confirm(`Are you sure you want to delete ${this.state.originalEntity!.name}? This will prevent members of this organization from accessing the system and also remove their data from reports.`);

        if (confirmed && this.props.match.params.id) {
            this.organizationDataService.delete(this.state.originalEntity!).then(
                () => {
                    this.props.history.push('/organization');
                },
                (err) => {
                    console.error(err);
                }
            );
        }
    }

    private midranges() {

        this.setState(
            {
                loading: true
            }, 
            () => {
                this.organizationDataService.calculateMidRanges(this.state.originalEntity ? this.state.originalEntity.id : 0).then(
                    () => {
                        this.props.notificationService.addInfo('Finished');
                        this.setState({
                            loading: false
                        });
                    },
                    (err: any) => {
                        this.props.notificationService.addError(err);
                        console.error(err);
                        this.setState({
                            loading: false
                        });
                    }
                );
            });
    }

    private async allUsersReport() {
        this.setState(
            { loading: true },
            async () => {
                if (!this.state.originalEntity) {
                    this.props.notificationService.addError('No organization selected');
                }
                try {
                    await this.organizationDataService.getAllUsersReport(this.state.originalEntity!.id);
                } catch (e) {
                    this.props.notificationService.addError('Error while running report');
                }
                this.setState({ loading: false });
            });
    }

    private pvastyles() {
        if (!this.state.originalEntity) {
            return;
        }

        this.setState(
            {
                loading: true
            }, 
            () => {
                this.organizationDataService.calculatePVA(this.state.originalEntity ? this.state.originalEntity.id : 0).then(
                    () => {
                        this.props.notificationService.addInfo('Finished');
                        this.setState({
                            loading: false
                        });
                    },
                    (err: any) => {
                        this.props.notificationService.addError(err);
                        console.error(err);
                        this.setState({
                            loading: false
                        });
                    }
                );
            });
    }
    
    private teamReport(candidates: boolean, former: boolean) {

        this.setState(
            {
                loading: true
            }, 
            () => {
                var fn = this.organizationDataService.calculateTeamReport;
                if (candidates) {
                    fn = this.organizationDataService.calculateTeamReportCandidates;
                } else if (former) {
                    fn = this.organizationDataService.calculateTeamReportFormer;
                }

                fn.bind(this.organizationDataService)(this.state.originalEntity ? this.state.originalEntity.id : 0).then(
                    () => {
                        this.props.notificationService.addInfo('Finished');
                        this.setState({
                            loading: false
                        });
                    },
                    (err: any) => {
                        this.props.notificationService.addError(err);
                        console.error(err);
                        this.setState({
                            loading: false
                        });
                    }
                );
            });
    }

    public render() {
        const { originalEntity } = this.state;
        if (!originalEntity || this.state.loading) {
            return <LoadingIndicator />;
        }

        const teams = originalEntity.teams.sort((a, b) => a.name.localeCompare(b.name));

        return (
            <div>
                <div className="content-header">
                    <div className="content-container">
                        <div className="title">
                            <NavLink to={`/organization`}>
                                <span className="glyphicon glyphicon-triangle-left" />
                            </NavLink>
                            &nbsp;
                            Edit Organization
                        </div>
                    </div>
                </div>

                <div className="content-body">
                    <div className="content-container">
                        <OrganizationForm defaultValues={originalEntity} submit={this.save} tenants={this.state.tenants} notificationService={this.props.notificationService} />
                        <hr />
                        <h3>Teams</h3>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {teams.map((team, index) => (
                                    <tr key={`team-${team.id}`}>
                                        <td>
                                            {team.name}
                                        </td>
                                        <td>
                                            <NavLink className="btn btn-default" to={`/team/${team.id}`}>View</NavLink>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <hr />
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-success" onClick={(e) => this.pvastyles()}><span className="glyphicon glyphicon-asterisk" /> Calculate PVA Styles</button>}
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-success" onClick={(e) => this.midranges()}><span className="glyphicon glyphicon-asterisk" /> Calculate Mid Ranges</button>}
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-success" onClick={(e) => this.teamReport(false, false)}><span className="glyphicon glyphicon-asterisk" /> Re-Calculate Team Report for All</button>}
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-success" onClick={(e) => this.teamReport(true, false)}><span className="glyphicon glyphicon-asterisk" /> Re-Calculate Team Report for Candidates</button>}
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-success" onClick={(e) => this.teamReport(false, true)}><span className="glyphicon glyphicon-asterisk" /> Re-Calculate Team Report for Former Employees</button>}
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-success" onClick={(e) => this.allUsersReport()}><span className="glyphicon glyphicon-asterisk" /> Export All-Users Report</button>}
                        <hr />
                        {originalEntity!.isDeactivated &&
                            <button className="btn btn-success" onClick={(e) => this.reactivate()}><span className="glyphicon glyphicon-play" /> Reactivate</button>
                        }
                        {!originalEntity!.isDeactivated &&
                            <button className="btn btn-warning" onClick={(e) => this.deactivate()}><span className="glyphicon glyphicon-pause" /> Deactivate</button>
                        }
                        &nbsp;&nbsp;
                        <button className="btn btn-danger" onClick={(e) => this.delete()}><span className="glyphicon glyphicon-remove" /> Delete</button>
                    </div>
                </div>
            </div>);
    }

    private save(values: any) {
        const tenant = this.state.tenants.find((t: Tenant) => {
            return t.id === values.tenantId;
        });

        const updatedOrganization = Object.assign(new Tenant(), values, { tenant: tenant });

        this.organizationDataService.update(updatedOrganization).then((success) => {
            this.props.history.push('/organization');
        });
    }
}


// WEBPACK FOOTER //
// ./src/components/organization/OrganizationEdit.tsx